import {
  clearAuthenticated,
  getStoredUserInfo,
  isStoredAuthenticated,
  registerUnauthorizedHandler,
  setAuthenticated,
  storeUserInfo,
} from '@api/api-client';
import type { AppUserInfo } from '@api/auth-api';
import { logout as logoutRequest } from '@api/auth-api';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sileo } from 'sileo';

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AppUserInfo | null;
  login: (userInfo: AppUserInfo) => void;
  logout: () => void;
  isSuperadmin: boolean;
  isAdmin: boolean;
  isEditor: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => isStoredAuthenticated());
  const [user, setUser] = useState<AppUserInfo | null>(
    () => getStoredUserInfo() as AppUserInfo | null,
  );

  const login = useCallback((userInfo: AppUserInfo) => {
    setAuthenticated();
    storeUserInfo(userInfo);
    setIsAuthenticated(true);
    setUser(userInfo);
  }, []);

  const logout = useCallback(() => {
    clearAuthenticated();
    setIsAuthenticated(false);
    setUser(null);
    void logoutRequest().catch(() => {});
  }, []);

  const handleUnauthorized = useCallback(() => {
    clearAuthenticated();
    setIsAuthenticated(false);
    setUser(null);
    sileo.error({ title: 'Session expired. Please log in again.' });
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    registerUnauthorizedHandler(handleUnauthorized);
  }, [handleUnauthorized]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      user,
      login,
      logout,
      isSuperadmin: user?.role === 'superadmin',
      isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
      isEditor: user?.role === 'editor',
    }),
    [isAuthenticated, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
